// DateFilter.js — ricerca temporale: chips per secolo + anni di precisione
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../LanguageContext';
import ArtworkDataService from '../../services/ArtworkService';

// Secoli coperti dal catalogo (arte bizantina): IV–XV
const CENTURIES = [
  { label: 'IV', n: 4 }, { label: 'V', n: 5 }, { label: 'VI', n: 6 },
  { label: 'VII', n: 7 }, { label: 'VIII', n: 8 }, { label: 'IX', n: 9 },
  { label: 'X', n: 10 }, { label: 'XI', n: 11 }, { label: 'XII', n: 12 },
  { label: 'XIII', n: 13 }, { label: 'XIV', n: 14 }, { label: 'XV', n: 15 },
];

const centuryStart = (n) => 100 * (n - 1) + 1;
const centuryEnd = (n) => 100 * n;

const DateFilter = ({ searchData_fine, searchData_inizio, onChangeSearchDataInizio, onChangeSearchDataFine, retrieveArtworks }) => {
  const { selectedLanguage } = useLanguage();
  // distribuzione delle opere per secolo (istogramma sopra le chips)
  const [histogram, setHistogram] = useState({});
  useEffect(() => {
    ArtworkDataService.getDateHistogram()
      .then((rows) => {
        const map = {};
        (rows || []).forEach((r) => { map[r.century] = r.count; });
        setHistogram(map);
      })
      .catch(() => { /* istogramma decorativo: nessun errore in UI */ });
  }, []);
  const maxCount = Math.max(1, ...Object.values(histogram));

  const from = parseInt(searchData_inizio, 10);
  const to = parseInt(searchData_fine, 10);
  const hasRange = !Number.isNaN(from) && !Number.isNaN(to);

  // un chip è attivo se il suo secolo è dentro il range corrente
  const isActive = (n) =>
    hasRange && from <= centuryStart(n) && to >= centuryEnd(n);

  // aggiorna stato e rilancia subito la ricerca con i nuovi estremi
  const applyRange = (start, end) => {
    const s = start === '' ? '' : String(start);
    const e = end === '' ? '' : String(end);
    onChangeSearchDataInizio({ target: { value: s } });
    onChangeSearchDataFine({ target: { value: e } });
    retrieveArtworks(1, { data_inizio: s, data_fine: e });
  };

  const toggleCentury = (n) => {
    const s = centuryStart(n);
    const e = centuryEnd(n);
    if (!hasRange) {
      applyRange(s, e);
    } else if (from === s && to === e) {
      // stesso secolo già selezionato: azzera
      applyRange('', '');
    } else if (s === to + 1 || e === from - 1 || isActive(n)) {
      // secolo adiacente o interno: estendi il range
      applyRange(Math.min(from, s), Math.max(to, e));
    } else {
      // secolo lontano: riparti da quello
      applyRange(s, e);
    }
  };

  return (
    <div className="mb-3">
      <div className="centuryStrip">
        {CENTURIES.map(({ label, n }) => {
          const count = histogram[n] || 0;
          const barHeight = Math.round((count / maxCount) * 26);
          const active = isActive(n);
          return (
            <div key={n} className="centuryCol"
                 title={`${label} sec. (${centuryStart(n)}–${centuryEnd(n)}): ${count} ${count === 1 ? 'opera' : 'opere'}`}>
              <div className={`centuryBar ${active ? 'active' : ''}`}
                   style={{ height: `${barHeight}px` }} />
              <button
                type="button"
                className={`centuryChip ${active ? 'active' : ''}`}
                aria-pressed={active}
                onClick={() => toggleCentury(n)}
              >
                {label}
              </button>
            </div>
          );
        })}
      </div>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder={selectedLanguage === 'en' ? 'From (year, e.g. 1100)' : 'Da (anno, es. 1100)'}
          value={searchData_inizio}
          onChange={onChangeSearchDataInizio}
          onKeyDown={(e) => { if (e.key === 'Enter') retrieveArtworks(1); }}
        />
        <input
          type="text"
          className="form-control"
          placeholder={selectedLanguage === 'en' ? 'To (year, e.g. 1250)' : 'A (anno, es. 1250)'}
          value={searchData_fine}
          onChange={onChangeSearchDataFine}
          onKeyDown={(e) => { if (e.key === 'Enter') retrieveArtworks(1); }}
        />
        <div className="input-group-append">
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => retrieveArtworks(1)}
          >
            {selectedLanguage === 'it' ? 'Cerca' : 'Search'}
          </button>
        </div>
      </div>
      <small className="dateHint">
        {selectedLanguage === 'it'
          ? 'Tocca i secoli per filtrare subito (i consecutivi si sommano); affina con gli anni e Cerca.'
          : 'Tap the centuries to filter instantly (consecutive ones add up); refine with years, then Search.'}
      </small>
    </div>
  );
};

export default DateFilter;
